import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Chip,
  Grid,
  IconButton,
  Typography,
  OutlinedInput,
  ListItemText,
  Divider,
  Paper,
} from "@mui/material";
import {
  Close,
  FilterList,
  Clear,
  Business,
  Work,
  LocationOn,
  Tune,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

const CustomFilterModal = ({
  open,
  onClose,
  onApplyFilter,
  onClearFilter,
  data = [],
}) => {
  const theme = useTheme();

  const [selectedDepartemen, setSelectedDepartemen] = useState([]);
  const [selectedJabatan, setSelectedJabatan] = useState([]);
  const [selectedCabang, setSelectedCabang] = useState([]);

  const clearAllFilters = () => {
    setSelectedDepartemen([]);
    setSelectedJabatan([]);
    setSelectedCabang([]);
    onClearFilter();
  };

  const getUniqueDepartemen = () => {
    return [
      ...new Set(
        data
          .map((row) => {
            const departemen = row.nama_jabatan;
            if (!departemen) return null;

            const parts = departemen.split(" - ");
            return parts[parts.length - 1].trim();
          })
          .filter(Boolean),
      ),
    ].sort();
  };

  const getUniqueJabatan = () => {
    return [
      ...new Set(
        data
          .map((row) => {
            const jabatan = row.nama_jabatan;
            if (!jabatan) return null;
            return jabatan.split(" - ")[0].trim();
          })
          .filter((val) => val),
      ),
    ].sort();
  };

  const getUniqueCabang = () => {
    return [
      ...new Set(
        data
          .map((row) => row.nama_cab)
          .filter((val) => val !== null && val !== undefined && val !== ""),
      ),
    ].sort();
  };

  const applyFilters = () => {
    const filterConfig = {
      filter: {
        nama_departemen: selectedDepartemen,
        nama_jabatan: selectedJabatan,
        nama_cab: selectedCabang,
      },
    };
    onApplyFilter(filterConfig);
    onClose();
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (selectedDepartemen.length > 0) count++;
    if (selectedJabatan.length > 0) count++;
    if (selectedCabang.length > 0) count++;
    return count;
  };

  const MenuItemStyle = {
    transition: "all 0.2s ease",
    borderRadius: 1,
    mx: 1,
    "&:hover": {
      backgroundColor: theme.palette.primary.light + "20",
      transform: "translateX(4px)",
    },
    "&.Mui-selected": {
      backgroundColor: theme.palette.primary.main + "15",
      "&:hover": {
        backgroundColor: theme.palette.primary.main + "25",
      },
    },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "90vh",
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 24px 48px rgba(0,0,0,0.15)",
        },
      }}
    >
      {/* Header with gradient */}
      <Box
        sx={{
          background: theme.palette.secondary.light,
          color: "white",
          p: 2.5,
          position: "relative",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                backgroundColor: "rgba(129, 92, 92, 0.2)",
                borderRadius: 2,
                p: 0.5,
                display: "flex",
              }}
            >
              <Tune sx={{ fontSize: 28 }} />
            </Box>
            <Typography variant="h6" fontWeight="600">
              Filter Data
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: "white",
              backgroundColor: "rgba(255,255,255,0.1)",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.25)",
              },
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </Box>

      <DialogContent
        sx={{
          p: 3,
          backgroundColor: theme.palette.grey[50],
        }}
      >
        {/* Active Filters Summary */}
        {getActiveFilterCount() > 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: theme.palette.primary,
              borderRadius: 2,
              border: `1px solid ${theme.palette.primary.main}30`,
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1.5}>
              <FilterList
                sx={{ color: theme.palette.primary.main, fontSize: 20 }}
              />
              <Typography
                variant="subtitle2"
                fontWeight="600"
                color={theme.palette.primary[400]}
              >
                Filter Aktif ({getActiveFilterCount()})
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {selectedDepartemen.map((val) => (
                <Chip
                  key={val}
                  label={val}
                  size="small"
                  onDelete={() =>
                    setSelectedDepartemen(
                      selectedDepartemen.filter((v) => v !== val),
                    )
                  }
                  sx={{
                    backgroundColor: theme.palette.secondary.main,
                    color: "white",
                    "& .MuiChip-deleteIcon": {
                      color: "white",
                      "&:hover": { color: theme.palette.primary.light },
                    },
                  }}
                />
              ))}
              {selectedJabatan.map((val) => (
                <Chip
                  key={val}
                  label={val}
                  size="small"
                  onDelete={() =>
                    setSelectedJabatan(selectedJabatan.filter((v) => v !== val))
                  }
                  sx={{
                    backgroundColor: theme.palette.secondary.main,
                    color: "white",
                    "& .MuiChip-deleteIcon": {
                      color: "white",
                      "&:hover": { color: theme.palette.secondary.light },
                    },
                  }}
                />
              ))}
              {selectedCabang.map((val) => (
                <Chip
                  key={val}
                  label={val}
                  size="small"
                  onDelete={() =>
                    setSelectedCabang(selectedCabang.filter((v) => v !== val))
                  }
                  sx={{
                    backgroundColor: theme.palette.secondary.main,
                    color: "white",
                    "& .MuiChip-deleteIcon": {
                      color: "white",
                      "&:hover": { color: theme.palette.secondary.light },
                    },
                  }}
                />
              ))}
            </Box>
          </Paper>
        )}

        {/* Filter Section */}
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: 2,
            p: 2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              mb: 2,
              fontWeight: "600",
              color: theme.palette.grey[700],
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Tune sx={{ fontSize: 20, color: theme.palette.primary.main }} />
            Filter Data
          </Typography>

          <Grid container spacing={2.5}>
            {/* Departemen */}
            <Grid item xs={12}>
              <FormControl
                fullWidth
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    },
                    "&.Mui-focused": {
                      boxShadow: "0 2px 12px rgba(0,0,0,0.12)",
                    },
                  },
                }}
              >
                <InputLabel>Departemen</InputLabel>
                <Select
                  multiple
                  value={selectedDepartemen}
                  onChange={(e) =>
                    setSelectedDepartemen(
                      typeof e.target.value === "string"
                        ? e.target.value.split(",")
                        : e.target.value,
                    )
                  }
                  input={<OutlinedInput label="Departemen" />}
                  renderValue={(selected) => (
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        py: 0.5,
                      }}
                    >
                      {selected.map((val) => (
                        <Chip
                          key={val}
                          label={val}
                          size="small"
                          sx={{
                            backgroundColor: theme.palette.grey.light,
                            color: theme.palette.grey.light,
                            fontWeight: 500,
                          }}
                        />
                      ))}
                    </Box>
                  )}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 300,
                        borderRadius: 2,
                        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                      },
                    },
                  }}
                >
                  {getUniqueDepartemen().map((departemen) => (
                    <MenuItem
                      key={departemen}
                      value={departemen}
                      sx={MenuItemStyle}
                    >
                      <Checkbox
                        size="small"
                        checked={selectedDepartemen.indexOf(departemen) > -1}
                        sx={{
                          color: theme.palette.primary.main,
                          "&.Mui-checked": {
                            color: theme.palette.primary.main,
                          },
                        }}
                      />
                      <Business
                        sx={{
                          mr: 1,
                          fontSize: 18,
                          color: theme.palette.grey[500],
                        }}
                      />
                      <ListItemText
                        primary={departemen}
                        primaryTypographyProps={{
                          fontSize: 14,
                        }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Jabatan */}
            <Grid item xs={12}>
              <FormControl
                fullWidth
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    },
                    "&.Mui-focused": {
                      boxShadow: "0 2px 12px rgba(0,0,0,0.12)",
                    },
                  },
                }}
              >
                <InputLabel>Jabatan</InputLabel>
                <Select
                  multiple
                  value={selectedJabatan}
                  onChange={(e) =>
                    setSelectedJabatan(
                      typeof e.target.value === "string"
                        ? e.target.value.split(",")
                        : e.target.value,
                    )
                  }
                  input={<OutlinedInput label="Jabatan" />}
                  renderValue={(selected) => (
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        py: 0.5,
                      }}
                    >
                      {selected.map((val) => (
                        <Chip
                          key={val}
                          label={val}
                          size="small"
                          sx={{
                            backgroundColor: theme.palette.grey.light,
                            color: theme.palette.grey.light,
                            fontWeight: 500,
                          }}
                        />
                      ))}
                    </Box>
                  )}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 300,
                        borderRadius: 2,
                        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                      },
                    },
                  }}
                >
                  {getUniqueJabatan().map((jabatan) => (
                    <MenuItem key={jabatan} value={jabatan} sx={MenuItemStyle}>
                      <Checkbox
                        size="small"
                        checked={selectedJabatan.indexOf(jabatan) > -1}
                        sx={{
                          color: theme.palette.secondary.main,
                          "&.Mui-checked": {
                            color: theme.palette.secondary.main,
                          },
                        }}
                      />
                      <Work
                        sx={{
                          mr: 1,
                          fontSize: 18,
                          color: theme.palette.grey[500],
                        }}
                      />
                      <ListItemText
                        primary={jabatan}
                        primaryTypographyProps={{
                          fontSize: 14,
                        }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Cabang */}
            <Grid item xs={12}>
              <FormControl
                fullWidth
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    },
                    "&.Mui-focused": {
                      boxShadow: "0 2px 12px rgba(0,0,0,0.12)",
                    },
                  },
                }}
              >
                <InputLabel>Cabang</InputLabel>
                <Select
                  multiple
                  value={selectedCabang}
                  onChange={(e) =>
                    setSelectedCabang(
                      typeof e.target.value === "string"
                        ? e.target.value.split(",")
                        : e.target.value,
                    )
                  }
                  input={<OutlinedInput label="Cabang" />}
                  renderValue={(selected) => (
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        py: 0.5,
                      }}
                    >
                      {selected.map((val) => (
                        <Chip
                          key={val}
                          label={val}
                          size="small"
                          sx={{
                            backgroundColor: theme.palette.grey.light,
                            color: theme.palette.grey.light,
                            fontWeight: 500,
                          }}
                        />
                      ))}
                    </Box>
                  )}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 300,
                        borderRadius: 2,
                        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                      },
                    },
                  }}
                >
                  {getUniqueCabang().map((cabang) => (
                    <MenuItem key={cabang} value={cabang} sx={MenuItemStyle}>
                      <Checkbox
                        size="small"
                        checked={selectedCabang.indexOf(cabang) > -1}
                        sx={{
                          color: theme.palette.warning.main,
                          "&.Mui-checked": {
                            color: theme.palette.warning.main,
                          },
                        }}
                      />
                      <LocationOn
                        sx={{
                          mr: 1,
                          fontSize: 18,
                          color: theme.palette.grey[500],
                        }}
                      />
                      <ListItemText
                        primary={cabang}
                        primaryTypographyProps={{
                          fontSize: 14,
                        }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <Divider sx={{ mx: 3 }} />

      <DialogActions
        sx={{
          p: 2.5,
          justifyContent: "space-between",
          backgroundColor: theme.palette.grey[50],
        }}
      >
        <Button
          onClick={clearAllFilters}
          startIcon={<Clear />}
          variant="outlined"
          color="inherit"
          sx={{
            borderColor: theme.palette.grey[300],
            color: theme.palette.grey[600],
            borderRadius: 1.5,
            px: 2,
            textTransform: "none",
            fontWeight: 500,
            "&:hover": {
              borderColor: theme.palette.error.main,
              color: theme.palette.error.main,
              backgroundColor: theme.palette.error.main + "10",
            },
          }}
        >
          Reset Filter
        </Button>
        <Box display="flex" gap={1.5}>
          <Button
            onClick={onClose}
            variant="text"
            sx={{
              color: theme.palette.grey[600],
              textTransform: "none",
              fontWeight: 500,
              px: 2,
              "&:hover": {
                backgroundColor: theme.palette.grey[200],
              },
            }}
          >
            Batal
          </Button>
          <Button
            onClick={applyFilters}
            variant="contained"
            startIcon={<FilterList />}
            sx={{
              background: theme.palette.secondary.main,
              color: "white",
              borderRadius: 1.5,
              px: 3,
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              "&:hover": {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
                transform: "translateY(-1px)",
              },
              transition: "all 0.2s ease",
            }}
          >
            Terapkan{" "}
            {getActiveFilterCount() > 0 && `(${getActiveFilterCount()})`}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CustomFilterModal;
